import React, { useId, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatDateShort, formatNumber } from '../../utils/format';
import { tokens } from '../../theme';

const W = 720;
const PAD = { top: 12, right: 12, bottom: 26, left: 48 };

const niceMax = (v) => {
  if (v <= 0) return 10;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
};

// Stock level over days: one series, recessive grid, crosshair + tooltip on hover.
// points: [{ day: 'YYYY-MM-DD', quantity }]; days without a snapshot are skipped, not zeroed.
const StockChart = ({ points = [], height = 220, color = tokens.primary, label = 'Остаток по дням' }) => {
  const id = useId().replace(/:/g, '');
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const geo = useMemo(() => {
    if (points.length === 0) return null;
    const max = niceMax(Math.max(...points.map((p) => p.quantity)));
    const iw = W - PAD.left - PAD.right;
    const ih = height - PAD.top - PAD.bottom;
    const x = (i) => PAD.left + (points.length === 1 ? iw / 2 : (iw * i) / (points.length - 1));
    const y = (v) => PAD.top + ih * (1 - v / max);
    const pts = points.map((p, i) => ({ ...p, x: x(i), y: y(p.quantity) }));
    return { max, pts, y, base: PAD.top + ih };
  }, [points, height]);

  if (!geo) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${tokens.borderStrong}`, borderRadius: '12px' }}>
        <Typography variant="body2" color="text.secondary">История начнёт копиться со следующего обновления остатков</Typography>
      </Box>
    );
  }
  const { pts, y, max, base } = geo;
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = pts.length > 1 ? `${line} L${pts[pts.length - 1].x},${base} L${pts[0].x},${base} Z` : '';
  const ticks = [0, max / 2, max];
  const xLabels = pts.length > 2 ? [pts[0], pts[Math.floor(pts.length / 2)], pts[pts.length - 1]] : pts;

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const sx = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    pts.forEach((p, i) => { if (Math.abs(p.x - sx) < Math.abs(pts[best].x - sx)) best = i; });
    setHover(best);
  };
  const h = hover != null ? pts[hover] : null;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="svg" ref={ref} viewBox={`0 0 ${W} ${height}`} role="img" aria-label={label}
        onMouseMove={onMove} onMouseLeave={() => setHover(null)}
        sx={{ width: '100%', height, display: 'block', overflow: 'visible', cursor: 'crosshair' }}>
        <defs>
          <linearGradient id={`sg${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={tokens.border} strokeDasharray={t ? '3 4' : undefined} />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={tokens.inkMuted}>{formatNumber(t)}</text>
          </g>
        ))}
        {xLabels.map((p, i) => (
          <text key={p.day} x={p.x} y={height - 6} fontSize="11" fill={tokens.inkMuted}
            textAnchor={i === 0 && xLabels.length > 1 ? 'start' : i === xLabels.length - 1 && xLabels.length > 1 ? 'end' : 'middle'}>
            {formatDateShort(p.day)}
          </text>
        ))}
        {area && <path d={area} fill={`url(#sg${id})`} />}
        {pts.length > 1 && <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {pts.length === 1 && <circle cx={pts[0].x} cy={pts[0].y} r="5" fill={color} stroke={tokens.paper} strokeWidth="2" />}
        {h && (
          <g pointerEvents="none">
            <line x1={h.x} x2={h.x} y1={PAD.top} y2={base} stroke={tokens.inkMuted} strokeDasharray="3 3" />
            <circle cx={h.x} cy={h.y} r="5" fill={color} stroke={tokens.paper} strokeWidth="2" />
          </g>
        )}
      </Box>
      {h && (
        <Box sx={{
          position: 'absolute', top: 0, left: `${(h.x / W) * 100}%`, transform: h.x > W * 0.7 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          bgcolor: tokens.ink, color: '#fff', px: 1.5, py: 1, borderRadius: '8px', pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: tokens.shadowRaised,
        }}>
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.8 }}>{formatDateShort(h.day)}</Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{formatNumber(h.quantity)} шт.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default StockChart;
